import { Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { BookOpen, MapPin, Clock, Heart, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { distanceKm, modeLabel } from "@/lib/constants";
import type { Coords } from "@/lib/location";
import { translateOption, useI18n } from "@/lib/i18n";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/lib/auth-context";

export type BookCard = {
  id: string;
  title: string;
  author: string;
  cover_url: string | null;
  category: string;
  age_group: string;
  exchange_duration: string;
  condition: string;
  lat: number | null;
  lng: number | null;
  status?: string;
  is_donation?: boolean;
  exchange_mode?: string | null;
  user_id?: string;
};

function statusLabel(status?: string) {
  if (!status || status === "available") return null;
  if (status === "swapped" || status === "exchanged") return "Swapped";
  if (status === "pending" || status === "reserved") return "Reserved";
  return "Not available";
}

function conditionTone(condition: string) {
  const c = condition.toLowerCase();
  if (c.includes("new") || c.includes("excellent")) return "bg-success/15 text-success";
  if (c.includes("good")) return "bg-primary/15 text-primary";
  if (c.includes("fair")) return "bg-warning/15 text-warning";
  return "bg-destructive/15 text-destructive";
}

export function BookCardItem({ book, here }: { book: BookCard; here?: Coords | null }) {
  const { user } = useAuth();
  const { lang } = useI18n();
  const [wished, setWished] = useState(false);
  const dist = here && book.lat != null && book.lng != null ? distanceKm(here.lat, here.lng, book.lat, book.lng) : null;
  const badge = statusLabel(book.status);
  const mode = (book.exchange_mode ?? (book.is_donation ? "donate" : "swap")) as "swap" | "lend" | "donate";
  const unavailable = badge !== null;

  useEffect(() => {
    if (!user) return;
    let cancel = false;
    supabase.from("wishlists").select("book_id").eq("user_id", user.id).eq("book_id", book.id).maybeSingle()
      .then(({ data }) => { if (!cancel) setWished(!!data); });
    return () => { cancel = true; };
  }, [user, book.id]);

  async function toggleWish(e: React.MouseEvent) {
    e.preventDefault();
    e.stopPropagation();
    if (!user) return toast.error(lang === "ar" ? "سجّل الدخول لحفظ الكتب في مفضلتك." : "Sign in to save books to your wishlist.");
    if (wished) {
      const { error } = await supabase.from("wishlists").delete().eq("user_id", user.id).eq("book_id", book.id);
      if (!error) { setWished(false); toast.success(lang === "ar" ? "تمت الإزالة من المفضلة" : "Removed from wishlist"); }
    } else {
      const { error } = await supabase.from("wishlists").insert({ user_id: user.id, book_id: book.id });
      if (!error) { setWished(true); toast.success(lang === "ar" ? "تم الحفظ في المفضلة 💗" : "Saved to wishlist 💗"); }
    }
  }

  return (
    <Link to="/book/$id" params={{ id: book.id }} className="group block">
      <div className="overflow-hidden rounded-2xl bg-card shadow-[var(--shadow-card)] book-card-tilt group-hover:shadow-[var(--shadow-elegant)]">
        <div className="relative aspect-[3/4] bg-primary-soft">
          {book.cover_url ? (
            <img
              src={book.cover_url}
              alt={book.title}
              className={`h-full w-full object-cover transition-transform duration-500 group-hover:scale-105 ${unavailable ? "opacity-60 grayscale" : ""}`}
              loading="lazy"
            />
          ) : (
            <div className="flex h-full items-center justify-center">
              <BookOpen className="h-12 w-12 text-primary/40" />
            </div>
          )}
          {/* spine highlight for cozy book feel */}
          <div className="pointer-events-none absolute inset-y-0 left-0 w-2 bg-gradient-to-r from-black/15 to-transparent" />
          <span className="absolute top-3 left-3 rounded-full bg-card/90 px-2 py-0.5 text-[10px] font-semibold text-primary backdrop-blur shadow-sm">
            {translateOption(book.category, lang)}
          </span>
          <span className={`absolute top-10 left-3 rounded-full px-2 py-0.5 text-[10px] font-semibold ${conditionTone(book.condition)} shadow-sm`}>
            {translateOption(book.condition, lang)}
          </span>
          {mode !== "swap" && (
            <span className="absolute top-3 right-3 rounded-full bg-success px-2 py-0.5 text-[10px] font-bold text-white shadow">
              {modeLabel(mode, lang)}
            </span>
          )}
          {!unavailable && mode === "swap" && (
            <button
              onClick={toggleWish}
              aria-label={wished ? (lang === "ar" ? "إزالة من المفضلة" : "Remove from wishlist") : (lang === "ar" ? "إضافة إلى المفضلة" : "Add to wishlist")}
              className="absolute top-3 right-3 grid h-8 w-8 place-items-center rounded-full bg-card/90 text-rose-500 shadow backdrop-blur transition hover:scale-110 dark:bg-elevated dark:text-primary"
            >
              <Heart className={`h-4 w-4 ${wished ? "fill-rose-500 dark:fill-primary" : ""}`} />
            </button>
          )}
          {badge && (
            <>
              <div className="absolute inset-0 bg-foreground/20" />
              <span className="absolute bottom-3 left-1/2 -translate-x-1/2 rounded-full bg-background/95 px-3 py-1 text-xs font-semibold text-foreground shadow-[var(--shadow-card)]">
                {badge === "Swapped" ? (lang === "ar" ? "تم تبادله بالفعل" : "Already swapped") : (lang === "ar" ? "غير متاح الآن" : "Not available right now")}
              </span>
            </>
          )}
          {dist !== null && dist < 1 && !unavailable && (
            <span className="absolute bottom-3 left-3 inline-flex items-center gap-1 rounded-full bg-emerald-500/95 px-2 py-0.5 text-[10px] font-semibold text-white shadow">
              <Sparkles className="h-3 w-3" /> {lang === "ar" ? "قريب جدًا" : "Very close"}
            </span>
          )}
        </div>
        <div className="p-4">
          <h3 className="line-clamp-1 font-serif text-base font-semibold text-foreground">{book.title}</h3>
          <p className="line-clamp-1 text-sm italic text-muted-foreground">{lang === "ar" ? `بقلم ${book.author}` : `by ${book.author}`}</p>
          <div className="mt-3 flex items-center gap-3 text-xs text-muted-foreground">
            {mode === "lend" && (
              <span className="inline-flex items-center gap-1"><Clock className="h-3 w-3" />{translateOption(book.exchange_duration, lang)}</span>
            )}
            {dist !== null && (
              <span className="inline-flex items-center gap-1"><MapPin className="h-3 w-3" />{dist.toFixed(1)} km</span>
            )}
          </div>
        </div>
      </div>
    </Link>
  );
}
